import { flushSync } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import classes from './ImpersonationBanner.module.css'
import { authActions } from '../../store/auth-slice'
import useResetState from '../../hooks/use-reset'
import { suppressStaleUnauthorized } from '../../functions/staleUnauthorized'
import { supportSessionFromToken, takeSupportReturn } from '../Support/supportApi'

const ImpersonationBanner = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { clearBusinessState, resetState } = useResetState()
  const url = useSelector((state) => state.auth.url)
  const isImpersonating = useSelector((state) => state.auth.isImpersonating)
  const impersonatedUserName = useSelector((state) => state.auth.impersonatedUserName)
  const companyName = useSelector((state) => state.auth.supportCompanyName || localStorage.getItem('sessionCompanyName'))
  const supportDisplayName = useSelector((state) => state.auth.supportDisplayName)
  const supportUserName = useSelector((state) => state.auth.supportUserName)

  if (!isImpersonating) {
    return null
  }

  const exit = async () => {
    // Rotate the support token first. Clearing company data while still
    // impersonating refetches with the old JWT; those 401s must not log us out.
    suppressStaleUnauthorized()
    try {
      const response = await fetch(url + 'api/support/exit-impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.access_token) {
        localStorage.removeItem('supportSessionToken')
        localStorage.removeItem('supportSessionCompany')
        const nextUserName = data.supportUserName || data.SupportUserName || supportUserName
        const returnTo = takeSupportReturn()
        // One render for both: rendered on its own, the session change makes the company page
        // the user is on redirect to the login page, which then sends support to Updates.
        flushSync(() => {
          dispatch(authActions.supportSession({
            ...supportSessionFromToken(data, {
              supportUserName: nextUserName,
              supportDisplayName: supportDisplayName || nextUserName,
              companyName,
              userName: nextUserName
            }),
            // Back on the support session, whatever the response says.
            isImpersonating: false,
            impersonatedUserName: ''
          }))
          // Back to the screen the connect started from: Clients list, client details or Connect.
          navigate(returnTo, { replace: true })
        })
        clearBusinessState()
        return
      }
    } catch (e) {
      // fall through to hard logout
    }
    // Never leave a user JWT active with the banner cleared — full logout.
    resetState()
    navigate('/support-login', { replace: true })
  }

  return (
    <div className={classes.banner} role="status">
      <span>
        Impersonating <strong>{impersonatedUserName || 'user'}</strong>
        {companyName ? <> @ <strong>{companyName}</strong></> : null}
        {supportDisplayName ? <> (as {supportDisplayName})</> : null}
      </span>
      <button type="button" className={classes.exit} onClick={exit}>Exit</button>
    </div>
  )
}

export default ImpersonationBanner
